import { GripVertical, MoreHorizontal } from 'lucide-react';
import { useState, type KeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { Button } from '../../../../components/Button';
import { MenuGroup, MenuItem, MenuSeparator, Popover, usePopover } from '../../../../components/Popover';
import styles from './Evaluation.module.css';

/**
 * Admin redesign batch 2 (Rubrics and Interview kits, Option A): reordering lines by dragging a handle, as the editable
 * table's rows are dragged (06-qol §14) — and, for the keyboard, Alt with ↑/↓ on the handle and a "Move to…" menu beside
 * each line (the critique: dragging alone is undiscoverable and hard on a keyboard).
 */

/** The list with one item taken from `from` and put at `to`. */
export function moveItem<T>(items: readonly T[], from: number, to: number): readonly T[] {
  if (from === to || from < 0 || from >= items.length || to < 0 || to >= items.length) return items;
  const next = [...items];
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return items;
  next.splice(to, 0, moved);
  return next;
}

interface DragState {
  readonly from: number;
  readonly to: number;
}

export interface DragReorder<TContainer extends HTMLElement> {
  /** A callback ref for the element holding the items; each item carries `data-reorder-item` (from `itemProps`). */
  readonly attach: (element: TContainer | null) => void;
  readonly itemProps: (index: number) => Readonly<Record<string, string | boolean | undefined>>;
  readonly handle: (index: number, label: string) => ReactNode;
}

/**
 * Pointer drag on a handle, measured against the items' boxes in the order they stand on screen, so one list can span
 * several groups (a kit's questions under their rubric lines). `onMove(from, to)` gets positions in that order.
 */
export function useDragReorder<TContainer extends HTMLElement>(count: number, onMove: (from: number, to: number) => void, isEnabled: boolean): DragReorder<TContainer> {
  const [container, setContainer] = useState<TContainer | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);

  function start(event: ReactPointerEvent<HTMLButtonElement>, from: number): void {
    if (!isEnabled || event.button !== 0) return;
    event.preventDefault();
    const handleElement = event.currentTarget;
    handleElement.setPointerCapture(event.pointerId);
    const items = Array.from(container?.querySelectorAll<HTMLElement>('[data-reorder-item]') ?? []);
    const targetAt = (clientY: number): number => {
      const index = items.findIndex((element) => {
        const box = element.getBoundingClientRect();
        return clientY < box.top + box.height / 2;
      });
      return index === -1 ? items.length - 1 : index;
    };
    setDrag({ from, to: from });
    const onPointerMove = (moveEvent: PointerEvent): void => setDrag({ from, to: targetAt(moveEvent.clientY) });
    const onPointerUp = (upEvent: PointerEvent): void => {
      handleElement.removeEventListener('pointermove', onPointerMove);
      handleElement.removeEventListener('pointerup', onPointerUp);
      handleElement.removeEventListener('pointercancel', onPointerUp);
      const to = targetAt(upEvent.clientY);
      setDrag(null);
      if (upEvent.type === 'pointerup' && to !== from) onMove(from, to);
    };
    handleElement.addEventListener('pointermove', onPointerMove);
    handleElement.addEventListener('pointerup', onPointerUp);
    handleElement.addEventListener('pointercancel', onPointerUp);
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number): void {
    if (!event.altKey || (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')) return;
    event.preventDefault();
    const to = index + (event.key === 'ArrowUp' ? -1 : 1);
    if (to >= 0 && to < count) onMove(index, to);
  }

  return {
    attach: setContainer,
    itemProps: (index) => ({
      'data-reorder-item': true,
      'data-dragged': drag?.from === index ? true : undefined,
      'data-drop': drag !== null && drag.to === index && drag.from !== index ? (drag.to > drag.from ? 'below' : 'above') : undefined,
    }),
    handle: (index, label) =>
      isEnabled ? (
        <button
          type="button"
          className={styles.handle}
          aria-label={`Drag ${label} to move it. Alt and the arrow keys move it too`}
          onPointerDown={(event) => start(event, index)}
          onKeyDown={(event) => onKeyDown(event, index)}
        >
          <GripVertical size={16} aria-hidden />
        </button>
      ) : null,
  };
}

/**
 * "Move to…": the line's own menu, beside its button (CLAUDE.md §5) — up, down, to the top or the bottom, any extra places
 * (a kit's other rubric lines), and Remove.
 */
export function MoveMenu({
  label,
  index,
  count,
  onMove,
  places,
  onRemove,
  removeLabel = 'Remove',
}: {
  readonly label: string;
  readonly index: number;
  readonly count: number;
  readonly onMove: (to: number) => void;
  /** More places to move to, under their own heading. */
  readonly places?: { readonly heading: string; readonly items: readonly { readonly label: string; readonly onSelect: () => void }[] };
  readonly onRemove?: () => void;
  readonly removeLabel?: string;
}): ReactNode {
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const act = (action: () => void): (() => void) => () => {
    close();
    action();
  };
  return (
    <>
      <Button ref={triggerRef} size="sm" variant="ghost" icon={MoreHorizontal} aria-label={`Move or remove ${label}`} aria-expanded={isOpen} onClick={toggle} />
      <Popover anchor={anchor} onClose={close} align="right" label={`Move or remove ${label}`}>
        <MenuGroup label="Move">
          <MenuItem disabled={index === 0} onClick={act(() => onMove(index - 1))}>
            Up one
          </MenuItem>
          <MenuItem disabled={index >= count - 1} onClick={act(() => onMove(index + 1))}>
            Down one
          </MenuItem>
          <MenuItem disabled={index === 0} onClick={act(() => onMove(0))}>
            To the top
          </MenuItem>
          <MenuItem disabled={index >= count - 1} onClick={act(() => onMove(count - 1))}>
            To the bottom
          </MenuItem>
        </MenuGroup>
        {places && places.items.length > 0 && (
          <MenuGroup label={places.heading}>
            {places.items.map((place) => (
              <MenuItem key={place.label} onClick={act(place.onSelect)}>
                {place.label}
              </MenuItem>
            ))}
          </MenuGroup>
        )}
        {onRemove && (
          <>
            <MenuSeparator />
            <MenuItem isDanger onClick={act(onRemove)}>
              {removeLabel}
            </MenuItem>
          </>
        )}
      </Popover>
    </>
  );
}
